import { ObjectType } from '@/@types/graphql-types';
import { generate, type ToVariants } from '@/components/experiments/Core/generateBasicExperimentComponent';
import { useExperimentStore, useUserStore } from '@/helpers/composables/useStores';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { computed } from 'vue';

// ?preferred_variant=FEWER_PROMOTED_TAGS_V1:control
// ?preferred_variant=FEWER_PROMOTED_TAGS_V1:variant_1
// ?preferred_variant=FEWER_PROMOTED_TAGS_V1:variant_2
// ?preferred_variant=FEWER_PROMOTED_TAGS_V1:variant_3
// ?preferred_variant=FEWER_PROMOTED_TAGS_V1:variant_4

// ?remove_preferred_experiment=FEWER_PROMOTED_TAGS_V1

export const FewerPromotedTagsExpName = 'FEWER_PROMOTED_TAGS_V1' as const;

export type FewerPromotedTagsExpVariants = ToVariants<typeof FewerPromotedTagsVariants>;

export const FewerPromotedTagsVariants = {
	CONTROL: 'control',
	VARIANT_1: 'variant_1', // Removing the Free Trial from the regular buybox (only showing FT in the episodes section)
	VARIANT_2: 'variant_2', // Merging the premium buybox into the regular buybox
	VARIANT_3: 'variant_3', // Using the winning variant from Exp 1 of the FreeTrial (without the promoted tag) on both buyboxes
	VARIANT_4: 'variant_4', // QTV in episodes
} as const;

export function getFewerPromotedTagsExpTagInformation(
	variant: FewerPromotedTagsExpVariants,
	country: string,
	isQualityTv: boolean = false
) {
	const shortVariant = getShortVariantName(variant);
	const processedCountry = country.toLowerCase();
	const promoType = getPromoType(variant, isQualityTv);
	return {
		variant: shortVariant,
		country: processedCountry,
		number: 21,
		promoType,
	};
}

const experimentStore = useExperimentStore();
const userStore = useUserStore();
export function useFewerPromotedTagsExpVariant(titleObjectType: ObjectType, offers: TitleDetail['offers']) {
	const variant = computed(() => {
		const hasPrimeVideoOffer = offers.filter(offer => [9, 119].includes(offer.package.packageId));
		if (hasPrimeVideoOffer.length) {
			// we only want this experiment on pages WITHOUT prime video offers
			return undefined;
		}

		if (userStore.isLoggedIn.value) {
			// we only want this experiment for logged OUT users
			return undefined;
		}

		if (titleObjectType === ObjectType.Show) {
			return experimentStore.activeVariantsWithControlGroup.value[FewerPromotedTagsExpName];
		}

		return undefined;
	});
	return {
		variant,
	};
}

export default generate(FewerPromotedTagsExpName, FewerPromotedTagsVariants);

function getShortVariantName(variant: FewerPromotedTagsExpVariants) {
	switch (variant) {
		case 'control':
			return 'control';
		case 'variant_1':
		case 'variant_2':
		case 'variant_3':
		case 'variant_4':
			const match = variant.match(/^variant_(\d+)$/);
			return match ? `v${match[1]}` : variant;
	}
}

function getPromoType(variant: FewerPromotedTagsExpVariants, isQualityTv: boolean) {
	switch (variant) {
		case 'control':
		case 'variant_1':
		case 'variant_2':
		case 'variant_3':
			return 'ft';
		case 'variant_4':
			return isQualityTv ? 'qtv' : 'ft';
	}
}
